import Link from "next/link";
import { useState } from "react";
import Router, { useRouter } from "next/router";
import classnames from "classnames";
import { Button, IconS3 } from "@aqar-app/aqar-design-system";
import Image from "next/image";
import actions from "../../../contexts/listingsList/listingsList.actions";
import {
  useListingListDispatch,
  useListingsListState,
} from "../../../contexts/listingsList/listingsList.provider";
import { useAuthState } from "../../../contexts/auth/auth.provider";
import { useAppState } from "../../../contexts/app/app.provider";
import {
  ConditionalWrapper,
  getPathLevel,
  stripTrailingSlash,
} from "../../../lib/helpers";
import { URL_LEVELS } from "../../../common/constants";
import sharedStyles from "../../../styles/shared.module.scss";
import config from "../../../common/config";
import {
  trackGA4Event,
  formatListingSearchFilters,
} from "../../../lib/tracking/gaHelpers";
import { events } from "../../../lib/tracking/constants";
import {
  trackAddNewListingEvent,
  trackDailyRentMode,
  trackOpenUserMenu,
} from "../../../lib/tracking/trackNavigationEvents";
import Modal from "../../../components/modal/Modal";
import { addPrefix } from "../../../lib/dailyRentHelper";
import styles from "./header.module.scss";

const cx = classnames.bind(styles);

const { toggleUserSidebar, toggleServicesPopup } = actions;

const Header = () => {
  const router = useRouter();
  const [modalProps, setModalProps] = useState({ visible: false });
  const dispatch = useListingListDispatch();
  const token = useAuthState("token");
  const { isMapPage, isDailyRentPage, msgsCount } = useAppState();
  const { whereFilters } = useListingsListState();
  const { category, iframe, params } = router.query;
  const pathLevel = params && getPathLevel(params);

  const togglePath = (routePath) => {
    const path = decodeURIComponent(routePath);
    return isDailyRentPage
      ? path.replace(isMapPage ? "/daily-map" : "/الحجز-اليومي", "")
      : path.substring(isMapPage ? 4 : 0);
  };

  const switchViewModeLink = addPrefix({
    isMapPage: !isMapPage,
    isDailyRentPage,
    originPath:
      !category || pathLevel?.level === URL_LEVELS.LISTING
        ? "/عقارات"
        : togglePath(router.asPath),
  });

  const switchDailyModeLink = addPrefix({
    isMapPage,
    isDailyRentPage: !isDailyRentPage,
    originPath: "/عقارات",
  });

  const handleUserMenuClick = () => {
    trackOpenUserMenu({
      isLoggedIn: !!token,
      hasNotifs: !!msgsCount,
    });
    if (token) {
      dispatch(toggleUserSidebar());
    } else {
      Router.push("/login");
    }
  };

  const pushChangeListingsViewEvent = () => {
    const event = {
      event: events.CHANGE_LISTING_LIST_VIEW,
      listing_view: isMapPage ? "list" : "map",
      listing_category: category,
      listing_search_filters: formatListingSearchFilters(whereFilters),
    };
    trackGA4Event(event);
  };

  const pushDailyRentViewEvent = () => {
    // eslint-disable-next-line no-unused-vars
    const event = {
      event: "daily_rent_view",
      listing_view: isDailyRentPage ? "list" : "daily-rent",
    };
    // dataLayerPushEvent(event);
  };

  const handleAddListing = () => {
    // FIXME: this is fired twice. Fix it on DS level.
    // FIXME: Add type to this event (is daily rent or normal listing)
    trackAddNewListingEvent({ source: "header" });
    if (isDailyRentPage)
      return setModalProps({
        visible: true,
        okLabel: "نعم",
        closeBtn: true,
        isError: true,
        headerIcon: "Warning-colored",
        children: (
          <div className={styles.modalContent}>
            لب ترغب في إضافة عقار للحجز اليومي
          </div>
        ),
        onOk: () => {
          setModalProps({ visible: false });
          window.open(
            encodeURI(
              "https://api.whatsapp.com/send?phone=966500507103&text=أرغب بعرض عقار للحجز الومي"
            )
          );
        },
        onCancel: () => setModalProps({ visible: false }),
      });
    return null;
  };

  const headerClassNames = classnames({
    [styles.header]: true,
    [sharedStyles.noTouchAction]: isMapPage,
    // eslint-disable-next-line eqeqeq
    [styles.iframe]: iframe == "1",
  });

  return (
    <div className={headerClassNames}>
      <Modal {...modalProps} />
      <div className={styles.rightMenu}>
        <IconS3
          alt="قائمة الخدمات"
          className={styles.serviceBtn}
          onClick={() => {
            dispatch(toggleServicesPopup());
            trackGA4Event({
              event: events.SHOW_POPUP_SERVICE,
            });
          }}
          type="ServiceList-blue"
          size="x-small"
          width={30}
          height={30}
          data-cy="open-services-popup"
        />
        <Link href="/" passHref>
          <a className={styles.logoWrapper}>
            <Image
              height={40}
              width={90}
              priority
              src={`${stripTrailingSlash(
                config.ICONS_BASE_URL
              )}/AqarLogoH-colored.svg`}
              alt="عقار"
              className={styles.logo}
            />
          </a>
        </Link>
      </div>
      <div className={styles.leftMenu}>
        <Link href={switchDailyModeLink || "/"} passHref>
          <Button
            priority={isDailyRentPage ? "primary" : "secondary"}
            className={cx(styles.dailyRentBtn, {
              [styles.daily]: !isDailyRentPage,
            })}
            suffixIcon={isDailyRentPage ? "aqarLogo" : "dailyRent"}
            iconColor={isDailyRentPage ? "white" : "colored"}
            size="x-small"
            label={isDailyRentPage ? "العودة لعقار" : "الحجز اليومي"}
            onClick={!isDailyRentPage && trackDailyRentMode}
          />
        </Link>
        {!isDailyRentPage && (
          <Link href={switchDailyModeLink || "/"} passHref>
            <Button
              type="icon"
              priority="secondary"
              className={styles.dailyRentBtn_m}
              size="x-small"
              onClick={trackDailyRentMode}
            >
              <IconS3
                alt="الحجز اليومي"
                type="dailyRent"
                color="colored"
                width={30}
                height={30}
              />
            </Button>
          </Link>
        )}
        <Link href={switchViewModeLink || "/"} passHref>
          <Button
            priority="secondary"
            className={styles.searchMapBtn}
            suffixIcon={isMapPage ? "Listings-ListView" : "MapSearch"}
            iconColor={isMapPage ? undefined : "colored"}
            size="x-small"
            label={isMapPage ? "بحث بالقائمة" : "بحث بالخريطة"}
            onClick={() => pushChangeListingsViewEvent()}
          />
        </Link>
        <Link href={switchViewModeLink || "/"} passHref>
          <Button
            type="icon"
            priority="secondary"
            className={styles.searchMapBtn_m}
            size="x-small"
            onClick={() => pushChangeListingsViewEvent()}
          >
            <IconS3
              alt="بحث بالخريطة"
              type={isMapPage ? "Listings-ListView_m" : "MapSearch"}
              color={isMapPage ? undefined : "colored"}
              width={30}
              height={30}
            />
          </Button>
        </Link>
        <ConditionalWrapper
          condition={!isDailyRentPage}
          wrapper={(children) => (
            <Link href="/add-listing/init/terms" passHref>
              {children}
            </Link>
          )}
        >
          <Button
            priority="secondary"
            size="x-small"
            className={styles.addLisingBtn}
            suffixIcon="add"
            iconColor="green"
            label="إضافة إعلان"
            onClick={handleAddListing}
          />
        </ConditionalWrapper>
        <ConditionalWrapper
          condition={!isDailyRentPage}
          wrapper={(children) => (
            <Link href="/add-listing/init/terms" passHref>
              {children}
            </Link>
          )}
        >
          <Button
            type="icon"
            size="x-small"
            className={styles.addLisingBtn_m}
            onClick={handleAddListing}
          >
            <IconS3
              alt="إضافة إعلان"
              type="SquareAdd"
              color="colored"
              width={30}
              height={30}
            />
          </Button>
        </ConditionalWrapper>
        <div style={{ position: "relative" }}>
          <Button
            priority="secondary"
            className={[styles.headerBtn, styles.userBtn]}
            size="x-small"
            onClick={handleUserMenuClick}
          >
            <IconS3 alt="معلومات الحساب" type="Profile" size="x-small" />
          </Button>
          {!!msgsCount && <sup className={styles.badgeDot} />}
        </div>
      </div>
    </div>
  );
};

export default Header;
